import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-center text-xl" }
const _hoisted_2 = { class: "font-bold" }

import debounce from 'lodash/debounce';
import { nextTick, onMounted, ref, watch } from 'vue';
import { usePagination } from '@/core/hooks/use-pagination';
import { BienBanGiaiTheService } from '@/core/services/BienBanGiaiThe.service';
import { useLoading } from '@/core/hooks/use-loading';
import { usePageHeader } from '@/core/hooks/usePageHeader';
import { swalErrNotification, swalNotification } from '@/core/helpers/utils';
import {
  IBienBanGiaiTheResponseBase,
  IVatTuNgoaiDinhMuc,
} from '@/core/interfaces/ApiResponses';
import { useButtonCustom } from '@/core/hooks/use-button-custom';

import BienBanGiaiTheTable from '@/components/shared/bien-ban-giai-the-table/BienBanGiaiTheTable.vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import Pagination from '@/components/shared/pagination/Pagination.vue';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog.vue';
import VatTuNgoaiDinhMucModal from '@/components/shared/vat-tu-ngoai-dinh-muc-modal/VatTuNgoaiDinhMucModal.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'List',
  setup(__props) {

document.title = 'Phê duyệt vật tư ngoài định mức - Đường sắt Hà Nội';

const { startLoading, endLoading } = useLoading();
const { currentPage, totalItems, perPage } = usePagination();
const items = ref<IBienBanGiaiTheResponseBase[]>([]);
const isConfirmDialogDisplay = ref(false);
const selectedBienBan = ref<IBienBanGiaiTheResponseBase | null>(null);
const approvalType = ref<'approve' | 'reject'>('approve');
const isApprovalLoading = ref(false);

const isModalVisible = ref(false);
const selectedBienBanForModal = ref<IBienBanGiaiTheResponseBase | null>(null);
const vatTuNgoaiDinhMucItems = ref<
  {
    id: number;
    ma_vat_tu: string;
    ten_vat_tu: string;
    don_vi: string;
    so_luong: number;
    ghi_chu: string;
    is_approved: number;
  }[]
>([]);
const isRefetching = ref(false);
const isApprovalInProgress = ref(false);
const isModalClosingManually = ref(false);
const vatTuTableRef = ref();

const { ButtonTypeColors, ButtonsType } = useButtonCustom();

const { searchQuery } = usePageHeader({
  title: 'Phê duyệt ngoài định mức',
  breadcrumbs: [
    { text: 'Phê duyệt' },
    { text: 'Ngoài định mức' },
  ],
  showSearch: true,
  searchPlaceholder: 'Tìm biên bản giải thể',
});

const actionButtons = ref([
  {
    buttonType: ButtonsType.DETAIL,
    colorType: ButtonTypeColors.WARNING,
    tooltipMessage: 'Chi tiết vật tư ngoài định mức',
    condition: () => true,
    onClick: (row: IBienBanGiaiTheResponseBase) => onDetailClick(row),
  },
]);

onMounted(async () => {
  await fetchBienBanGiaiTheItems();
});

watch(
  () => currentPage.value,
  () => {
    fetchBienBanGiaiTheItems();
  }
);

watch(
  () => perPage.value,
  async () => {
    await nextTick();

    if (currentPage.value === 1) {
      fetchBienBanGiaiTheItems();
      return;
    }
    currentPage.value = 1;
  }
);

watch(searchQuery, debounce(() => {
  fetchBienBanGiaiTheItems();
}, 500));

const fetchBienBanGiaiTheItems = async () => {
  try {
    await startLoading();

    const {
      data: {
        data: { data, total },
      },
    } = await BienBanGiaiTheService.listVatTuCongTyDuyet({
      current_page: currentPage.value,
      per_page: perPage.value,
      ...(searchQuery.value && { searchTerm: searchQuery.value }),
    });

    items.value = data;
    totalItems.value = total;

    await endLoading();
  } catch (error) {
    await endLoading();

    await swalErrNotification(
      error,
      'Có lỗi xảy ra khi lấy danh sách biên bản giải thể'
    );
  }
};

const onDetailClick = async (bienBan: IBienBanGiaiTheResponseBase) => {
  try {
    await startLoading();

    selectedBienBanForModal.value = bienBan;

    const {
      data: {
        data: { data: ngoai_dinh_muc },
      },
    } = await BienBanGiaiTheService.getVatTuCongTyDuyetById(bienBan.id, {
      current_page: 1,
      per_page: 1000,
    });

    vatTuNgoaiDinhMucItems.value = ngoai_dinh_muc.map((item) => {
      return {
        id: item.id,
        ma_vat_tu: item.ma_vat_tu,
        ten_vat_tu: item.ten_vat_tu,
        don_vi: item.don_vi_tinh,
        so_luong: item.so_luong,
        ghi_chu: item.ghi_chu || '',
        is_approved: item.is_approved === 1 ? 1 : 0,
      };
    });

    isModalVisible.value = true;

    await endLoading();
  } catch (error) {
    console.log('error: ', error);
    await endLoading();

    await swalErrNotification(
      error,
      'Có lỗi xảy ra khi tải dữ liệu vật tư ngoài định mức'
    );
  }
};

const onModalVisibilityChange = async (isVisible: boolean) => {
  if (!isVisible && isModalClosingManually.value) {
    isApprovalInProgress.value = false;
    isModalClosingManually.value = false;
    return;
  }

  if (!isVisible && selectedBienBanForModal.value && !isRefetching.value) {
    await refetchVatTuNgoaiDinhMucData(selectedBienBanForModal.value.id);
  }

  if (!isVisible && !isRefetching.value && !isApprovalInProgress.value) {
    await fetchBienBanGiaiTheItems();
  }

  if (!isVisible) {
    isApprovalInProgress.value = false;
    isModalClosingManually.value = false;
  }
};

const refetchVatTuNgoaiDinhMucData = async (bienBanId: number) => {
  if (isRefetching.value) {
    return;
  }

  try {
    isRefetching.value = true;
    await startLoading();

    const {
      data: {
        data: { data: ngoai_dinh_muc },
      },
    } = await BienBanGiaiTheService.getVatTuCongTyDuyetById(bienBanId, {
      current_page: 1,
      per_page: 1000,
    });

    vatTuNgoaiDinhMucItems.value = ngoai_dinh_muc.map((item) => {
      return {
        id: item.id,
        ma_vat_tu: item.ma_vat_tu,
        ten_vat_tu: item.ten_vat_tu,
        don_vi: item.don_vi_tinh,
        so_luong: item.so_luong,
        ghi_chu: item.ghi_chu || '',
        is_approved: item.is_approved === 1 ? 1 : 0,
      };
    });

    if (vatTuTableRef.value?.refreshTableData) {
      vatTuTableRef.value.refreshTableData();
    }

    await endLoading();
  } catch (error) {
    await endLoading();
    console.log('Error refetching data: ', error);
  } finally {
    isRefetching.value = false;
  }
};

const handleApproveVatTu = async (approvedItems: IVatTuNgoaiDinhMuc[]) => {
  console.log('Vat tu approved:', approvedItems);

  isApprovalInProgress.value = true;

  setTimeout(() => {
    isApprovalInProgress.value = false;
  }, 5000);
};

const handleModalManualClose = () => {
  isModalClosingManually.value = true;
  isModalVisible.value = false;
  selectedBienBanForModal.value = null;
  vatTuNgoaiDinhMucItems.value = [];
};

const handleApprovalCompleted = async () => {
  await fetchBienBanGiaiTheItems();
};

const confirmApproval = async () => {
  if (!selectedBienBan.value) return;

  try {
    isApprovalLoading.value = true;
    await startLoading();

    if (approvalType.value === 'approve') {
      await BienBanGiaiTheService.approve(selectedBienBan.value.id);
      await endLoading();
      await swalNotification('Duyệt biên bản giải thể thành công', 'success');
    } else {
      await BienBanGiaiTheService.reject(selectedBienBan.value.id);
      await endLoading();
      await swalNotification('Từ chối biên bản giải thể thành công', 'success');
    }

    isConfirmDialogDisplay.value = false;
    selectedBienBan.value = null;

    await fetchBienBanGiaiTheItems();
  } catch (error) {
    await endLoading();

    await swalErrNotification(
      error,
      'Có lỗi xảy ra khi xử lý biên bản giải thể'
    );
  } finally {
    isApprovalLoading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ConfirmationDialog, {
      title: "Xác nhận",
      size: "sm",
      "is-display": isConfirmDialogDisplay.value,
      onOnConfirmButtonClick: confirmApproval,
      onOnCloseButtonClick: _cache[0] || (_cache[0] = ($event: any) => (isConfirmDialogDisplay.value = false)),
      onOnCloseIconClick: _cache[1] || (_cache[1] = ($event: any) => (isConfirmDialogDisplay.value = false))
    }, {
      default: _withCtx(() => [
        _createElementVNode("h4", _hoisted_1, [
          _createTextVNode(" Bạn có chắc chắn muốn duyệt BBGT số "),
          _createElementVNode("span", _hoisted_2, _toDisplayString(selectedBienBan.value?.so_bien_ban), 1),
          _createTextVNode("? ")
        ])
      ]),
      _: 1
    }, 8, ["is-display"]),
    _createVNode(VatTuNgoaiDinhMucModal, {
      ref_key: "vatTuTableRef",
      ref: vatTuTableRef,
      "is-visible": isModalVisible.value,
      "onUpdate:isVisible": [
        _cache[2] || (_cache[2] = ($event: any) => ((isModalVisible).value = $event)),
        onModalVisibilityChange
      ],
      "bien-ban-info": selectedBienBanForModal.value,
      "vat-tu-items": vatTuNgoaiDinhMucItems.value,
      onApproveVatTu: handleApproveVatTu,
      onManualClose: handleModalManualClose,
      onApprovalCompleted: handleApprovalCompleted
    }, null, 8, ["is-visible", "bien-ban-info", "vat-tu-items"]),
    _createVNode(PageContentBase, null, {
      default: _withCtx(() => [
        _createVNode(BienBanGiaiTheTable, {
          "is-ke-hoach-column-displaying": true,
          items: items.value,
          "action-buttons": actionButtons.value,
          "cur-page": _unref(currentPage),
          "per-page": _unref(perPage),
          onOnDetailClick: _cache[3] || (_cache[3] = (({ id }) => _ctx.$router.push({
        name: 'chi-tiet-bien-ban-giai-the',
        params: { id }
      })))
        }, null, 8, ["items", "action-buttons", "cur-page", "per-page"]),
        (items.value.length > 0)
          ? (_openBlock(), _createBlock(Pagination, {
              key: 0,
              page: _unref(currentPage),
              "page-size": _unref(perPage),
              total: _unref(totalItems),
              onChangePage: _cache[4] || (_cache[4] = (value) => (currentPage.value = value)),
              onChangePageSize: _cache[5] || (_cache[5] = (value) => (perPage.value = value))
            }, null, 8, ["page", "page-size", "total"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})